// ========================================
// 🧬 PROMPT PARA ANÁLISE DE MARCADORES TUMORAIS
// Foco: Curva completa de AFP e tendências
// ========================================

// 💧 B312/AI_141 — diuréticos comuns no cirrótico (DCB), para detecção
// determinística no texto de "medicações em uso". Serve para o prompt
// correlacionar a natriurese (sódio urinário baixo com/sem diurético muda a
// leitura). NÃO decide conduta — só informa o contexto ("IA lê, código decide").
const DIURETICOS_CONHECIDOS = [
  "espironolactona",
  "furosemida",
  "hidroclorotiazida",
  "clortalidona",
  "indapamida",
  "amilorida",
  "triantereno",
  "bumetanida",
  "torasemida",
  "torsemida",
  "eplerenona",
  "xipamida",
  "metolazona",
];

/** Detecta diuréticos citados num texto livre de medicações (acento-insensível). */
export function detectarDiureticos(texto?: string): string[] {
  if (!texto) return [];
  const t = texto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return DIURETICOS_CONHECIDOS.filter((d) => t.includes(d));
}

export function gerarPromptMarcadores(
  paciente: any,
  marcadores: any[],
  // 🧂 B312/AI_141 — sódio SÉRICO mais recente (dos exames lab), para o prompt
  // correlacionar com o sódio urinário. Opcional: se ausente, a IA faz a leitura
  // possível e cita como próximo passo (não trava).
  sodioSericoRecente?: { valor: number | string; data: string } | null
): string {
  // Ordenar marcadores por data (mais antigo primeiro = cronológico)
  const marcadoresOrdenados = [...marcadores].sort(
    (a, b) =>
      new Date(a.dataColeta).getTime() - new Date(b.dataColeta).getTime()
  );

  const formatarData = (data: string) => {
    if (!data) return "N/A";
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  };

  // 📏 B312-FU2/AI_141 — regra "TODAS + teto generoso" (decisão de Leonardo).
  // Por padrão usa TODAS as coletas (cada uma ≈ 1 linha; a curva importa e o
  // custo/latência é irrelevante — B256). Só como salvaguarda contra prompts
  // gigantes: acima de LIMITE_COLETAS, mantém as N MAIS RECENTES por extenso e
  // sinaliza quantas antigas foram omitidas. A curva de AFP (compacta) segue
  // COMPLETA logo abaixo, então a cinética tumoral não se perde nem no teto.
  const LIMITE_COLETAS = 15;
  const coletasUsadas =
    marcadoresOrdenados.length > LIMITE_COLETAS
      ? marcadoresOrdenados.slice(-LIMITE_COLETAS)
      : marcadoresOrdenados;
  const coletasOmitidas = marcadoresOrdenados.length - coletasUsadas.length;

  // Formatar as coletas usadas em ordem cronológica
  const historicoCompleto = coletasUsadas
    .map((m, i) => {
      const partes: string[] = [];
      if (m.afp !== undefined && m.afp !== null && m.afp !== "")
        partes.push(`AFP: ${m.afp} ng/mL`);
      if (m.ca199 !== undefined && m.ca199 !== null && m.ca199 !== "")
        partes.push(`CA 19-9: ${m.ca199} U/mL`);
      if (m.cea !== undefined && m.cea !== null && m.cea !== "")
        partes.push(`CEA: ${m.cea} ng/mL`);
      if (m.ca125 !== undefined && m.ca125 !== null && m.ca125 !== "")
        partes.push(`CA 125: ${m.ca125} U/mL`);
      if (m.psa !== undefined && m.psa !== null && m.psa !== "")
        partes.push(`PSA: ${m.psa}`);
      // Metabolismo do ferro (B241 fase 2 — AI_109)
      if (m.ferro !== undefined && m.ferro !== null && m.ferro !== "")
        partes.push(`Ferro sérico: ${m.ferro} µg/dL`);
      if (m.ferritina !== undefined && m.ferritina !== null && m.ferritina !== "")
        partes.push(`Ferritina: ${m.ferritina} ng/mL`);
      if (
        m.saturacaoTransferrina !== undefined &&
        m.saturacaoTransferrina !== null &&
        m.saturacaoTransferrina !== ""
      )
        partes.push(`Saturação de transferrina: ${m.saturacaoTransferrina} %`);
      // Metabólicos / Etiológicos (B195)
      if (
        m.cobreSerico !== undefined &&
        m.cobreSerico !== null &&
        m.cobreSerico !== ""
      )
        partes.push(`Cobre sérico: ${m.cobreSerico} µg/dL`);
      if (
        m.cobreUrinario !== undefined &&
        m.cobreUrinario !== null &&
        m.cobreUrinario !== ""
      )
        partes.push(`Cobre urinário (24h): ${m.cobreUrinario} µg/24h`);
      if (
        m.ceruloplasmina !== undefined &&
        m.ceruloplasmina !== null &&
        m.ceruloplasmina !== ""
      )
        partes.push(`Ceruloplasmina: ${m.ceruloplasmina} mg/dL`);
      if (
        m.alfa1Antitripsina !== undefined &&
        m.alfa1Antitripsina !== null &&
        m.alfa1Antitripsina !== ""
      )
        partes.push(`Alfa-1 antitripsina: ${m.alfa1Antitripsina} mg/dL`);
      // Função renal / urina 24h (B279 — AI_120)
      if (
        m.clearanceCreatinina !== undefined &&
        m.clearanceCreatinina !== null &&
        m.clearanceCreatinina !== ""
      )
        partes.push(
          `Clearance de creatinina (total): ${m.clearanceCreatinina} mL/min`
        );
      if (
        m.clearanceCreatininaCorrigido !== undefined &&
        m.clearanceCreatininaCorrigido !== null &&
        m.clearanceCreatininaCorrigido !== ""
      )
        partes.push(
          `Clearance de creatinina (corrigido 1,73m²): ${m.clearanceCreatininaCorrigido} mL/min/1,73m²`
        );
      if (
        m.proteinuria24h !== undefined &&
        m.proteinuria24h !== null &&
        m.proteinuria24h !== ""
      )
        partes.push(`Proteinúria de 24h: ${m.proteinuria24h} mg/24h`);
      if (
        m.sodioUrinarioAmostra !== undefined &&
        m.sodioUrinarioAmostra !== null &&
        m.sodioUrinarioAmostra !== ""
      )
        partes.push(
          `Sódio urinário (amostra isolada): ${m.sodioUrinarioAmostra} mEq/L`
        );
      if (
        m.sodioUrinario24h !== undefined &&
        m.sodioUrinario24h !== null &&
        m.sodioUrinario24h !== ""
      )
        partes.push(`Sódio urinário (24h): ${m.sodioUrinario24h} mEq/24h`);
      // Imunoglobulinas / Autoimune-IgG4 (B195)
      if (
        m.iggSerico !== undefined &&
        m.iggSerico !== null &&
        m.iggSerico !== ""
      )
        partes.push(`IgG sérico: ${m.iggSerico} mg/dL`);
      if (m.igg4 !== undefined && m.igg4 !== null && m.igg4 !== "")
        partes.push(`IgG4: ${m.igg4} mg/dL`);
      // Eletroforese de proteínas (frações em g/dL) — B195
      const efpPartes: string[] = [];
      if (
        m.efpAlbumina !== undefined &&
        m.efpAlbumina !== null &&
        m.efpAlbumina !== ""
      )
        efpPartes.push(`Alb ${m.efpAlbumina}`);
      if (m.efpAlfa1 !== undefined && m.efpAlfa1 !== null && m.efpAlfa1 !== "")
        efpPartes.push(`α1 ${m.efpAlfa1}`);
      if (m.efpAlfa2 !== undefined && m.efpAlfa2 !== null && m.efpAlfa2 !== "")
        efpPartes.push(`α2 ${m.efpAlfa2}`);
      if (m.efpBeta !== undefined && m.efpBeta !== null && m.efpBeta !== "")
        efpPartes.push(`β ${m.efpBeta}`);
      if (m.efpGama !== undefined && m.efpGama !== null && m.efpGama !== "")
        efpPartes.push(`γ ${m.efpGama}`);
      if (efpPartes.length > 0)
        partes.push(`Eletroforese (g/dL): ${efpPartes.join(", ")}`);
      // 🔬 Autoanticorpos (autoimunidade hepática) — B312-FU2/AI_141. Antes
      // NÃO chegavam à IA (a Categoria C saía "sem valores" mesmo com FAN 1/160).
      // Valores qualitativos (título "1/160", "Negativo", padrão "p-ANCA").
      const AUTOANTICORPOS: [string, string][] = [
        ["fan", "FAN/ANA"],
        ["antiMusculoLiso", "Anti-Músculo Liso (ASMA)"],
        ["antiLKM", "Anti-LKM1"],
        ["antiMitocondria", "Anti-Mitocôndria (AMA)"],
        ["anca", "ANCA"],
        ["antiDNA", "Anti-DNA nativo"],
        ["antiTransglutaminase", "Anti-Transglutaminase (anti-tTG)"],
      ];
      for (const [campo, label] of AUTOANTICORPOS) {
        if (m[campo] !== undefined && m[campo] !== null && m[campo] !== "")
          partes.push(`${label}: ${m[campo]}`);
      }
      return `  ${i + 1}. ${formatarData(m.dataColeta)}: ${
        partes.length > 0 ? partes.join(" | ") : "Sem valores"
      }`;
    })
    .join("\n");

  // Extrair apenas valores de AFP para análise de curva
  const valoresAFP = marcadoresOrdenados
    .filter((m) => m.afp !== undefined && m.afp !== null && m.afp !== "")
    .map((m) => ({
      data: formatarData(m.dataColeta),
      valor: parseFloat(m.afp),
    }));

  const curvaAFP =
    valoresAFP.length > 0
      ? valoresAFP.map((v) => `  ${v.data}: ${v.valor} ng/mL`).join("\n")
      : "  Nenhum valor de AFP registrado";

  // Dados do paciente
  const temSE = paciente.inscricaoTx?.situacaoEspecial === true;
  const tipoSE = paciente.inscricaoTx?.tipoSE || "";
  const diagnosticoBase =
    paciente.inscricaoTx?.diagnosticoBase || "Não informado";

  // 🧂 B312/AI_141 — contexto para INTERPRETAR o sódio urinário / função renal.
  // A natriurese só ganha leitura plena junto de sódio SÉRICO, DIURÉTICO em uso e
  // ASCITE. Este bloco só entra quando há algum dado renal/urinário nos marcadores
  // (não polui a análise quando irrelevante) e é escopado a esses achados.
  const naoVazio = (v: any) => v !== undefined && v !== null && v !== "";
  const ascite = paciente.avaliacaoClinica?.ascite === true;
  const diureticos = detectarDiureticos(
    paciente.avaliacaoClinica?.medicacoesEmUso || paciente.medicacoesEmUso || ""
  );
  const temDadoRenal = marcadoresOrdenados.some(
    (m) =>
      naoVazio(m.sodioUrinario24h) ||
      naoVazio(m.sodioUrinarioAmostra) ||
      naoVazio(m.clearanceCreatinina) ||
      naoVazio(m.clearanceCreatininaCorrigido) ||
      naoVazio(m.proteinuria24h)
  );
  const blocoContextoRenal = temDadoRenal
    ? `

CONTEXTO PARA INTERPRETAR SÓDIO URINÁRIO / FUNÇÃO RENAL (use SOMENTE para correlacionar esses achados; não é análise global):
- Sódio sérico mais recente: ${
        sodioSericoRecente && naoVazio(sodioSericoRecente.valor)
          ? `${sodioSericoRecente.valor} mEq/L (${formatarData(
              sodioSericoRecente.data
            )})`
          : "não disponível"
      }
- Diurético(s) em uso: ${
        diureticos.length ? diureticos.join(", ") : "nenhum registrado"
      }
- Ascite: ${ascite ? "SIM" : "não registrada"}`
    : "";

  return `
ANÁLISE DE MARCADORES SÉRICOS - CURVA E TENDÊNCIAS POR CATEGORIA
================================================================

CONTEXTO DO PACIENTE:
- Sexo: ${paciente.sexo === "M" ? "Masculino" : "Feminino"}
- Diagnóstico de base: ${diagnosticoBase}
- Situação especial: ${temSE ? `SIM (${tipoSE})` : "NÃO"}${blocoContextoRenal}

HISTÓRICO DE MARCADORES (${
    marcadoresOrdenados.length
  } coletas no total${
    coletasOmitidas > 0
      ? `; exibindo as ${coletasUsadas.length} mais recentes — ${coletasOmitidas} coleta(s) mais antiga(s) omitida(s) por brevidade`
      : ""
  }, ordem cronológica):
${historicoCompleto}

CURVA DE AFP (todos os valores, mesmo os de coletas antigas omitidas acima):
${curvaAFP}

═══════════════════════════════════════════════════════════════

INSTRUÇÕES - FOCO EXCLUSIVO NOS MARCADORES SÉRICOS:

Analise EXCLUSIVAMENTE os marcadores listados acima e suas tendências,
organizando a resposta por CATEGORIA. Comente APENAS as categorias que
têm pelo menos um valor registrado (ignore as vazias).
NÃO faça análise global do paciente.
NÃO recomende exames de imagem, tratamentos ou condutas terapêuticas.

── CATEGORIA A — MARCADORES TUMORAIS (AFP, CA 19-9, CEA, CA 125, PSA) ──

1. CURVA COMPLETA DE AFP:
   - Descreva a trajetória COMPLETA: todos os valores, do primeiro ao último
   - Identifique fases: ascensão, pico, queda, estabilidade, nova elevação
   - Se houve queda após pico elevado: correlacionar com possível tratamento (TACE, ablação, quimioembolização)
   - Calcular velocidade de variação entre coletas consecutivas quando relevante
   - Se houve queda seguida de nova elevação: alertar para possível recidiva tumoral

2. SIGNIFICADO CLÍNICO DA CURVA DE AFP:
   - AFP <20 ng/mL: sem valor diagnóstico, flutuação normal
   - AFP 20-200: zona intermediária, monitorizar tendência
   - AFP 200-400: fortemente sugestivo de CHC ativo
   - AFP >400: praticamente diagnóstico de CHC
   - AFP >1000: alto risco de invasão vascular e metástase, critério de drop-out
   - TENDÊNCIA é MAIS importante que valor absoluto isolado

3. CA 19-9, CEA, CA 125, PSA (se disponíveis):
   - Descrever tendência de cada marcador
   - Se CA 19-9 elevado: mencionar diferencial com colangiocarcinoma

── CATEGORIA B — METABÓLICOS / ETIOLÓGICOS (ferro, cobre, ceruloplasmina, A1AT) ──

0. METABOLISMO DO FERRO (hemocromatose / sobrecarga de ferro):
   - Ferro sérico (ref ~65-175 µg/dL), ferritina (ref ~30-300; homem até ~400) e
     saturação de transferrina (ref ~20-50%).
   - PADRÃO DE SOBRECARGA: saturação de transferrina ELEVADA (> 45%, sobretudo
     > 60%) + ferritina ELEVADA → sugere hemocromatose (investigar HFE) ou
     sobrecarga secundária. A saturação alta é o marcador mais precoce/sensível.
   - Ferritina é reagente de fase aguda: ELEVADA isolada (saturação normal) pode
     ser inflamação/necrose hepática/síndrome metabólica, não sobrecarga real.
   - Pós-Tx: sobrecarga de ferro pode persistir/recidivar; correlacione.

4. COBRE E CERULOPLASMINA (doença de Wilson):
   - Cobre sérico normal: 70-140 µg/dL | Cobre urinário (24h): < 60 µg/24h
   - Ceruloplasmina normal: 20-35 mg/dL
   - PADRÃO DE WILSON: ceruloplasmina BAIXA (< 20) + cobre urinário 24h ELEVADO
     (> 100) ± cobre sérico baixo/normal. Correlacione os três; isoladamente
     cada um tem baixa especificidade. Sinalize se o padrão sugere Wilson.
   - Em paciente já tratado para Wilson, o cobre urinário pode subir por
     mobilização (efeito do quelante) — interprete conforme o contexto.

5. ALFA-1 ANTITRIPSINA (deficiência de A1AT):
   - Normal: 90-200 mg/dL. BAIXA (< 90, sobretudo muito reduzida): sugere
     deficiência de A1AT como etiologia da hepatopatia (correlacionar com
     fenótipo/genótipo se disponível; valor isolado não fecha diagnóstico).

5b. FUNÇÃO RENAL / URINA 24h (clearance de creatinina, proteinúria):
   - Clearance de creatinina (depuração/TFG por urina de 24h): use o valor CORRIGIDO
     para 1,73m² para classificar a função renal (normal ~90-120; < 60 mL/min/1,73m²
     sugere doença renal crônica). O valor TOTAL (não corrigido) reflete a depuração
     absoluta do paciente naquela superfície corporal.
   - Relevância em hepatologia/transplante: a creatinina compõe o MELD; disfunção renal
     no cirrótico levanta síndrome hepatorrenal × nefropatia × pré-renal (correlacione
     com sódio, diurético, PBE/infecção). Pós-Tx, queda do clearance pede revisar
     nefrotoxicidade do inibidor de calcineurina (tacrolimo/ciclosporina).
   - Proteinúria de 24h: normal < 150 mg/24h. ELEVADA sugere lesão renal (glomerular se
     significativa) — considere glomerulopatia associada a HBV/HCV, nefropatia por IgA
     na cirrose ou dano por inibidor de calcineurina. Não diagnostica isolada;
     correlacione com clearance e contexto clínico.

5c. SÓDIO URINÁRIO (avidez renal por sódio — dado CENTRAL no cirrótico):
   - CLASSIFIQUE o valor PRIMEIRO. NUNCA responda apenas "não interpretável isoladamente":
     mesmo sem todos os correlatos, dê a leitura possível.
   - Sódio urinário de 24h (mEq/24h) É a excreção TOTAL do dia e JÁ INTEGRA o volume
     urinário — NÃO exija "volume urinário" para lê-lo (essa ressalva só vale para a
     amostra isolada em mEq/L). Sob dieta hipossódica (~88 mEq/dia = 2 g de sódio), a
     natriurese ADEQUADA é > 78 mEq/24h. Valor BAIXO (< 78 mEq/24h) = RETENÇÃO ávida de
     sódio (ativação do SRAA/simpático da cirrose) e/ou balanço de sódio positivo
     (tendência a formar/acumular ascite). Na AMOSTRA isolada, Na < 10–20 mEq/L reforça
     retenção intensa / padrão pré-renal (síndrome hepatorrenal).
   - LEITURAS ACIONÁVEIS (use o CONTEXTO fornecido — sódio sérico, diurético, ascite):
     • SEM diurético em uso → a restrição de sódio isolada tende a ser insuficiente.
     • COM diurético em uso → natriurese baixa sugere resposta inadequada / balanço
       positivo → reavaliar dose e adesão à dieta; vigiar ascite refratária se a dose já
       for alta.
     Se algum correlato faltar, faça a leitura possível e cite o dado ausente como
     PRÓXIMO PASSO — não trave. Enquadre como marcador de gravidade neuro-humoral
     (correlato de risco de HRS-AKI / pior prognóstico), SEM superdimensionar nem
     inventar valores que não estejam nos dados.

── CATEGORIA C — IMUNOLOGIA / AUTOIMUNIDADE (IgG, IgG4, eletroforese, AUTOANTICORPOS) ──

6. IgG E ELETROFORESE (hepatite autoimune):
   - IgG normal: 700-1600 mg/dL. ELEVADA: dado de apoio à hepatite autoimune
     (interpretar junto com autoanticorpos, se houver).
   - Eletroforese de proteínas (frações em g/dL — albumina 3,5-5,0 | α1 0,1-0,4 |
     α2 0,5-1,0 | β 0,6-1,2 | gama 0,7-1,6):
     • Gamaglobulina ELEVADA (hipergamaglobulinemia): policlonal apoia
       autoimunidade/cirrose; suspeita de banda/pico monoclonal exige outra
       investigação (mieloma, Waldenström).
     • Albumina BAIXA: déficit hepatossintético / inflamação.
     • Fusão beta-gama (bridging β-γ): sugestiva de cirrose.
     Interprete o CONJUNTO das frações, não cada uma isolada.

7. IgG4 (doença relacionada a IgG4):
   - Normal: < 135 mg/dL. ELEVADA: levantar colangite/doença esclerosante
     IgG4-relacionada no diferencial (correlacionar com imagem de vias biliares
     quando houver). Elevação leve isolada tem baixa especificidade.

7b. AUTOANTICORPOS (hepatopatias autoimunes — SEMPRE comentar quando houver valor):
   - Valores são qualitativos: título (ex.: "1/160", "Negativo"), padrão (ANCA:
     "c-ANCA"/"p-ANCA") ou numérico. Título ≥ 1/80 costuma ser considerado positivo
     relevante em adultos (o LSR pode variar por laboratório).
   - FAN/ANA e Anti-Músculo Liso (ASMA): apoiam HEPATITE AUTOIMUNE tipo 1 (junto de
     IgG elevada). Anti-LKM1: HAI tipo 2.
   - Anti-Mitocôndria (AMA): marcador da COLANGITE BILIAR PRIMÁRIA (CBP) —
     praticamente patognomônico quando positivo.
   - ANCA (p-ANCA/atípico): associa-se à COLANGITE ESCLEROSANTE PRIMÁRIA (CEP) e à
     doença inflamatória intestinal; interpretar no contexto.
   - Anti-DNA nativo: lúpus / overlap. Anti-Transglutaminase (anti-tTG): doença
     celíaca (pode coexistir com hepatopatia autoimune).
   - Regra: se QUALQUER autoanticorpo tiver valor, a Categoria C NÃO é "sem dados" —
     comente-o. Um perfil positivo (ex.: FAN + ASMA + IgG alta) sugere um padrão;
     negativos relevantes também merecem registro (ex.: AMA negativo afasta CBP como
     hipótese principal). Não fechar diagnóstico com um autoanticorpo isolado.

── INTEGRAÇÃO ──

8. INTERPRETAÇÃO INTEGRADA (apenas categorias com dados):
   - O que as curvas/valores sugerem em cada categoria com dados?
   - Tendência (subindo/descendo/estável) e próxima dosagem/intervalo sugeridos.

NÃO INCLUIR: análise de função hepática, recomendações de imagem, condutas
terapêuticas globais, status de Milão, critérios de drop-out detalhados.
Foque APENAS na interpretação dos marcadores e no que significam clinicamente.
`.trim();
}
